/**
 * Color swatch picker for the learner experience.
 * Wraps React Aria ColorSwatchPicker with a 16-color preset palette.
 */
import { ColorSwatchPicker, ColorSwatchPickerItem, ColorSwatch } from 'react-aria-components';

export const FOLDER_COLORS = [
  { name: 'Slate', value: '#64748b' },
  { name: 'Red', value: '#ef4444' },
  { name: 'Orange', value: '#f97316' },
  { name: 'Amber', value: '#f59e0b' },
  { name: 'Yellow', value: '#eab308' },
  { name: 'Lime', value: '#84cc16' },
  { name: 'Green', value: '#22c55e' },
  { name: 'Emerald', value: '#10b981' },
  { name: 'Teal', value: '#14b8a6' },
  { name: 'Cyan', value: '#06b6d4' },
  { name: 'Sky', value: '#0ea5e9' },
  { name: 'Blue', value: '#3b82f6' },
  { name: 'Indigo', value: '#6366f1' },
  { name: 'Violet', value: '#8b5cf6' },
  { name: 'Purple', value: '#a855f7' },
  { name: 'Pink', value: '#ec4899' },
] as const;

export const DEFAULT_FOLDER_COLOR = FOLDER_COLORS[13].value; // Violet

export interface ColorPalettePickerProps {
  value?: string | null;
  onChange: (color: string) => void;
  label?: string;
}

export function ColorPalettePicker({
  value,
  onChange,
  label = 'Folder color',
}: ColorPalettePickerProps) {
  return (
    <ColorSwatchPicker
      aria-label={label}
      value={value ?? undefined}
      onChange={(c) => onChange(c.toString('hex'))}
      style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(8, 1fr)',
        gap: 6,
        padding: 8,
      }}
    >
      {FOLDER_COLORS.map((c) => (
        <ColorSwatchPickerItem key={c.value} color={c.value}>
          {({ isSelected, isFocusVisible }) => (
            <ColorSwatch
              style={{
                width: 24,
                height: 24,
                borderRadius: 6,
                cursor: 'pointer',
                outline: isFocusVisible ? '2px solid var(--color-accent)' : 'none',
                outlineOffset: 2,
                boxShadow: isSelected
                  ? `0 0 0 2px var(--color-surface), 0 0 0 4px ${c.value}`
                  : '0 1px 2px rgba(0,0,0,0.1)',
                transition: 'box-shadow 0.15s, transform 0.15s',
                transform: isSelected ? 'scale(1.1)' : undefined,
              }}
            />
          )}
        </ColorSwatchPickerItem>
      ))}
    </ColorSwatchPicker>
  );
}
